import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ReactPaginate from 'react-paginate';
import { useNavigate } from 'react-router-dom';

import { useDispatch, useSelector } from 'react-redux';
import { NavigationService } from '../services/navigation.service';
import { GlobalService } from '../services/global.service';

import { UserService } from '../services/user.service';
import { AvatarService } from '../services/avatar.service';
import { ClientService } from '../services/client.service';
import { SiteService } from '../services/site.service';
import { SectorService } from '../services/sector.service';
import { SkillService } from '../services/skill.service';
import { AbilityService } from '../services/ability.service';

import usePersistantState from 'hooks/state.hook';

import { USER_COLUMN } from 'constants/import.constants';

import { Ability, AVAILABLE_STEPS, Button, Client, CommonGlobalService, CommonUtilsService, DEFAULT_STEP, DownloadIcon, RootState, Skill, UploadIcon, UserFilter, UserUmanforge, UUID } from '@umanforge/common';

import { HomeTopBar } from '../components/menu/home_top_bar.component';
import { UserComponent } from '../components/users/user.component';
import { SeeMore } from '../components/inputs/see_more.component';
import { SearchInput } from '../components/inputs/search_input.component';
import { Select } from '../components/inputs/select.component';
import { Loading } from '../components/loading.component';
import { HomeLeftBar } from 'components/menu/home_left_bar.component';

import { PlusIcon } from '../components/icons/plus_icon.component';
import { MinusIcon } from '../components/icons/minus_icon.component';
import { UsersIcon } from '../components/icons/users_icon.component';
import { ExportModal } from 'features/import_export/components/modal_export.component';
import { ImportModal } from 'features/import_export/components/modal_import.component';
import { ImportHelperModal } from 'features/import_export/components/import_helper_modal.component';


export function UsersView() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const dispatch = useDispatch();
  const token = useSelector( ( state: RootState ) => { return state.token.value; } );
  const me = useSelector( ( state: RootState ) => { return state.user.value; } );

  /*----------------------------- useState ------------------------------*/
  const [ filterName, setFilterName ] = usePersistantState<string>( 'user_filterName', '' );
  const [ filterClients, setFilterClients ] = usePersistantState<UUID[]>( 'user_filterClients', [] );
  const [ filterSites, setFilterSites ] = usePersistantState<UUID[]>( 'user_filterSites', [] );
  const [ filterSectors, setFilterSectors ] = usePersistantState<UUID[]>( 'user_filterSectors', [] );
  const [ filterSkills, setFilterSkills ] = usePersistantState<UUID[]>( 'user_filterSkills', [] );
  const [ filterAbilities, setFilterAbilities ] = usePersistantState<UUID[]>( 'user_filterAbilities', [] );
  const [ filterDisabled, setFilterDisabled ] = usePersistantState<boolean>( 'user_filterDisabled', false );
  const [ filterLocked, setFilterLocked ] = usePersistantState<boolean>( 'user_filterLocked', false );

  const [ total, setTotalUsers ] = useState<number>( 0 );
  const [ page, setPage ] = usePersistantState<number>( 'user_page', 0 );
  const [ step, setStep ] = usePersistantState<number>( 'user_step', DEFAULT_STEP );
  const [ users, setUsers ] = useState<UserUmanforge[]>( [] );

  const [ isLoading, setIsLoading ] = useState<boolean>( true );

  const [ isFirstRender, setIsFirstRender ] = useState<boolean>( true );
  const [ isFilterEnd, setIsFilterEnd ] = useState<boolean>( false );

  const [ seeMoreClients, setSeeMoreClients ] = useState<boolean>( false );
  const [ seeMoreSkills, setSeeMoreSkills ] = useState<boolean>( false );
  const [ seeMoreAbilities, setSeeMoreAbilities ] = useState<boolean>( false );

  const [ skills, setSkills ] = useState<Skill[]>( [] );
  const [ abilities, setAbilities ] = useState<Ability[]>( [] );
  const [ clients, setClients ] = useState<( Client & { isCollapsed: boolean } )[]>( [] );
  const [ avatars, setAvatars ] = useState<Record<UUID, string | null>>( {} );

  const [ isOpenExportModal, setIsOpenExportModal ] = useState<boolean>( false );
  const [ isOpenImportModal, setIsOpenImportModal ] = useState<boolean>( false );

  /*---------------------------- useEffect ------------------------------*/
  useEffect( () => {
    if ( me ) {
      refreshFilters();
    }
  }, [ me ] );

  useEffect( () => {
    if ( isFilterEnd ) {
      checkClientFilter();
      checkSitesFilter();
      checkSectorsFilter();
      checkAbilitiesFilter();
      checkSkillsFilter();
      setTimeout( () => { setIsFirstRender( false ); }, 75 );
    }
  }, [ isFilterEnd ] );

  useEffect( () => {
    if ( !isFirstRender ) {
      refreshUsers();
    }
  }, [ isFirstRender, page, step, filterName, filterSkills, filterAbilities, filterClients, filterSites, filterSectors, filterDisabled, filterLocked ] );

  useEffect( () => {
    if ( !isFirstRender ) {
      setSeeMoreClients( filterClients.length > 0 || filterSites.length > 0 || filterSectors.length > 0 );
    }
  }, [ isFirstRender, filterClients, filterSites, filterSectors ] );

  useEffect( () => {
    if ( !isFirstRender ) {
      setSeeMoreAbilities( filterAbilities.length > 0 );
    }
  }, [ isFirstRender, filterAbilities ] );

  useEffect( () => {
    if ( !isFirstRender ) {
      setSeeMoreSkills( filterSkills.length > 0 );
    }
  }, [ isFirstRender, filterSkills ] );

  /*---------------------------- Functions ------------------------------*/
  const refreshFilters = async (): Promise<void> => {
    const getUpdatedClients = async (): Promise<Client[]> => {
      const updatedClients = await ClientService.getAllowedClients( null, token, dispatch );
      const updatedSites = await SiteService.getAllowedSites( null, token, dispatch );
      const updatedSectors = await SectorService.getAllowedSectors( null, token, dispatch );
      CommonUtilsService.sortByKey( updatedSectors, 'name', 'asc' );
      CommonUtilsService.sortByKey( updatedSites, 'name', 'asc' );
      CommonUtilsService.sortByKey( updatedClients, 'name', 'asc' );
      for ( const site of updatedSites ) {
        site.sectors = updatedSectors.filter( sector => { return sector.siteId === site.id; } );
      }
      for ( const client of updatedClients ) {
        client.sites = updatedSites.filter( site => { return site.clientId === client.id; } );
      }

      return updatedClients;
    };

    const getUpdatedSkills = async (): Promise<Skill[]> => {
      const responseSkills = await SkillService.getAllowSkills( null, token, dispatch );
      CommonUtilsService.sortByKey( responseSkills, 'name', 'asc' );
      return responseSkills;
    };

    const getUpdatedAbilities = async (): Promise<Ability[]> => {
      const responseAbilities = await AbilityService.getAllowAbilities( null, token, dispatch );
      CommonUtilsService.sortByKey( responseAbilities, 'name', 'asc' );
      return responseAbilities;
    };

    await Promise.all( [ getUpdatedClients(), getUpdatedSkills(), getUpdatedAbilities() ] )
      .then( ( [ updatedClients, updatedSkills, updatedAbilities ] ) => {
        setClients( updatedClients.map( client => {
          const isCollapsed = me?.isSuperAdmin != true;
          return { ...client, isCollapsed };
        } ) );
        setSkills( updatedSkills );
        setAbilities( updatedAbilities );
      } );

    setIsFilterEnd( true );
  };

  const refreshUsers = async (): Promise<void> => {
    setIsLoading( true );
    const filters: UserFilter = {
      page: page + 1,
      step,
      filterDisabled,
      filterLocked,
      filterName: filterName && filterName != '' ? filterName : undefined,
      filterClients: filterClients && filterClients.length > 0 ? filterClients : undefined,
      filterSites: filterSites && filterSites.length > 0 ? filterSites : undefined,
      filterSectors: filterSectors && filterSectors.length > 0 ? filterSectors : undefined,
      filterSkills: filterSkills && filterSkills.length > 0 ? filterSkills : undefined,
      filterAbilities: filterAbilities && filterAbilities.length > 0 ? filterAbilities : undefined
    };
    const response = await UserService.getAllowedUsers( filters, token, dispatch );
    if ( response != null ) {
      if ( response.users.length == 0 && page != 0 ) {
        setPage( Math.ceil( response.total / step ) - 1 );
      } else {
        setUsers( response.users );
        setTotalUsers( response.total );

        const clientId = me?.clients?.[ 0 ]?.id as UUID | undefined;
        if ( clientId && response.users.length > 0 ) {
          const usersId = response.users.map( user => { return user.id; } );
          const avatarsResponse = await AvatarService.getUsersAvatars( clientId, usersId, token, dispatch );
          if ( avatarsResponse ) {
            const avatarMap: Record<UUID, string | null> = {};
            avatarsResponse.forEach( item => {
              avatarMap[ item.userId ] = item.avatar ?? null;
            } );
            setAvatars( avatarMap );
          } else {
            setAvatars( {} );
          }
        } else {
          setAvatars( {} );
        }
      }
    }
    setTimeout(  () => { setIsLoading( false ); }, 75 );
  };

  const checkClientFilter = (): void => {
    const newFilter = filterClients
      .filter( clientIdFilter => {
        return clients.find( client => { return client.id === clientIdFilter; } ) != null;
      } );
    if ( newFilter.length != filterClients.length ) {
      setFilterClients( newFilter );
    }
  };

  const checkSitesFilter = (): void => {
    const sites = clients.map( client => { return client.sites ?? []; } ).flat();
    const newFilter = filterSites
      .filter( siteIdFilter => {
        return sites.find( site => { return site.id === siteIdFilter; } ) != null;
      } );
    if ( newFilter.length != filterSites.length ) {
      setFilterSites( newFilter );
    }
  };

  const checkSectorsFilter = (): void => {
    const sectors = clients.map( client => { return GlobalService.getClientSectors( client ); } ).flat();
    const newFilter = filterSectors
      .filter( sectorIdFilter => {
        return sectors.find( sector => { return sector.id === sectorIdFilter; } ) != null;
      } );
    if ( newFilter.length != filterSectors.length ) {
      setFilterSectors( newFilter );
    }
  };

  const checkAbilitiesFilter = (): void => {
    const newFilter = filterAbilities
      .filter( abilityIdFilter => {
        return abilities.find( ability => { return ability.id === abilityIdFilter; } ) != null;
      } );
    if ( newFilter.length != filterAbilities.length ) {
      setFilterAbilities( newFilter );
    }
  };

  const checkSkillsFilter = (): void => {
    const newFilter = filterSkills
      .filter( skillIdFilter => {
        return skills.find( skill => { return skill.id === skillIdFilter; } ) != null;
      } );
    if ( newFilter.length != filterSkills.length ) {
      setFilterSkills( newFilter );
    }
  };

  /*------------------------------ Events -------------------------------*/
  const onChangeFilteredClients = ( clientId: UUID, value: boolean ) => {
    if ( value ) {
      setFilterClients( [ ...filterClients, clientId ] );
    } else {
      const copyFilterClients = [ ...filterClients ];
      const index = copyFilterClients.findIndex( item => { return item === clientId; } );
      if ( index != -1 ) {
        copyFilterClients.splice( index, 1 );
      }
      setFilterClients( copyFilterClients );
    }
  };

  const onChangeFilteredSites = ( siteId: UUID, value: boolean ) => {
    if ( value ) {
      setFilterSites( [ ...filterSites, siteId ] );
    } else {
      const copyFilterSites = [ ...filterSites ];
      const index = copyFilterSites.findIndex( item => { return item === siteId; } );
      if ( index != -1 ) {
        copyFilterSites.splice( index, 1 );
      }
      setFilterSites( copyFilterSites );
    }
  };

  const onChangeFilteredSectors = ( sectorId: UUID, value: boolean ) => {
    if ( value ) {
      setFilterSectors( [ ...filterSectors, sectorId ] );
    } else {
      const copyFilterSectors = [ ...filterSectors ];
      const index = copyFilterSectors.findIndex( item => { return item === sectorId; } );
      if ( index != -1 ) {
        copyFilterSectors.splice( index, 1 );
      }
      setFilterSectors( copyFilterSectors );
    }
  };

  const onChangeFilteredSkills = ( skillId: UUID, value: boolean ) => {
    if ( value ) {
      setFilterSkills( [ ...filterSkills, skillId ] );
    } else {
      const copyFilterSkills = [ ...filterSkills ];
      const index = copyFilterSkills.findIndex( item => { return item === skillId; } );
      if ( index != -1 ) {
        copyFilterSkills.splice( index, 1 );
      }
      setFilterSkills( copyFilterSkills );
    }
  };

  const onChangeFilteredAbilities = ( abilityId: UUID, value: boolean ) => {
    if ( value ) {
      setFilterAbilities( [ ...filterAbilities, abilityId ] );
    } else {
      const copyFilterAbilities = [ ...filterAbilities ];
      const index = copyFilterAbilities.findIndex( item => { return item === abilityId; } );
      if ( index != -1 ) {
        copyFilterAbilities.splice( index, 1 );
      }
      setFilterAbilities( copyFilterAbilities );
    }
  };

  const openExportModal = () => {
    setIsOpenExportModal( true );
  };
  const closeExportModal = () => {
    setIsOpenExportModal( false );
  };

  const openImportModal = () => {
    setIsOpenImportModal( true );
  };
  const closeImportModal = ( isImported?: boolean ) => {
    setIsOpenImportModal( false );
    if ( isImported === true ) {
      refreshUsers();
    }
  };


  /*------------------------------- Front -------------------------------*/
  return (
    <>
      <HomeLeftBar items={ NavigationService.getNavBarItems( token, dispatch ) } needConfirmation={ false } />
      <div className='w-full h-full'>
        <div>
          <HomeTopBar title={ t( 'User' ) } needConfirmationToNav={ false }/>
        </div>
        <div className='flex h-full gap-1 mx-2'>
          <div className='flex flex-col gap-10 pt-10 w-[250px] h-full'>
            {
              me && ( CommonGlobalService.isClientAdmin( me ) || CommonGlobalService.isSiteAdmin( me ) || CommonGlobalService.isSectorAdmin( me ) ) ?
                <div className='flex flex-col w-[250px]'>
                  <Button
                    label={ t( 'New user' ) }
                    className='w-full m-1 bg-[#0BB173] hover:bg-[#088D5C] hover:ring-1 hover:ring-[#088D5C] text-sm font-medium text-white'
                    icon={ <PlusIcon className='w-[24px] h-[24px] p-0.5 fill-white' /> }
                    onClick={ () => { NavigationService.toNewUser( navigate, false ); } }
                  />
                  {
                    CommonGlobalService.isClientAdmin( me ) ?
                      <div className='flex'>
                        <div className='flex items-center w-full'>
                          <Button
                            label={ t( 'common:export' ) }
                            className='w-full m-1 bg-[#0BB173] hover:bg-[#088D5C] hover:ring-1 hover:ring-[#088D5C] text-sm font-medium text-white'
                            icon={ <DownloadIcon className='w-[24px] h-[24px] p-0.5 text-white'/> }
                            onClick={ openExportModal }
                          />
                        </div>
                        <div className='flex items-center'>
                          <Button
                            label={ t( 'common:import' ) }
                            className='w-full m-1 bg-[#0BB173] hover:bg-[#088D5C] hover:ring-1 hover:ring-[#088D5C] text-sm font-medium text-white'
                            icon={ <UploadIcon className='w-[24px] h-[24px] p-0.5 text-white'/> }
                            onClick={ openImportModal }
                          />
                          <ImportHelperModal
                            title={ t( 'import_help_title' ) }
                            text={
                              <div className='space-y-2 text-sm text-gray-700'>
                                <p className='font-semibold text-gray-800'>{ t( 'template_columns_intro' ) }</p>
                                <ul className='list-disc pl-5 space-y-1'>
                                  {
                                    Object.values( USER_COLUMN )
                                      .map( column => {
                                        return ( <li key={ column }> { t( column ).trim() } </li> );
                                      } )
                                  }
                                </ul>
                              </div>
                            }
                            api={ () => {return UserService.downloadUsersTemplate( token, dispatch );} }
                          />
                        </div>
                      </div>
                      :
                      null
                  }
                </div>
                :
                null
            }
            {
              clients.length != 0 ?
                <div className='relative border p-1 rounded-lg text-left h-fit w-full'>
                  <div className='absolute w-fit px-1.5 -top-3.5 left-3 bg-white font-bold'>{ t( 'Client' ) }</div>
                  <div className='px-4 py-2 text-sm'>
                    {
                      <div>
                        {
                          clients.slice( 0, seeMoreClients ? clients.length : 5 ).map( client => {
                            return (
                              <div key={ client.id } className='flex flex-col mb-1'>
                                <div className='flex gap-2 items-center select-none'>
                                  <input
                                    type='checkbox'
                                    id={ client.id }
                                    className='h-fit cursor-pointer'
                                    checked={ filterClients.find( clientId => { return clientId === client.id; } ) != null }
                                    onChange={ e => { onChangeFilteredClients( client.id, ( e.target as HTMLInputElement ).checked ); } }
                                  />
                                  <label htmlFor={ client.id } className='font-medium cursor-pointer truncate'>{ client.name }</label>
                                  <div
                                    style={{ display: me?.isSuperAdmin == true ? '' : 'none' }}
                                    onClick={ () => {
                                      client.isCollapsed = !client.isCollapsed;
                                      setClients( structuredClone( clients ) );
                                    } }
                                  >
                                    {
                                      client.sites && client.sites.length != 0 ?
                                        <div className='w-[18px] h-[18px] bg-gray-200 p-1 ml-1 rounded-full'>
                                          {
                                            client.isCollapsed === true || GlobalService.clientOrChildrensIsFilter( client, { clients: filterClients, sites: filterSites, sectors: [] } ) ?
                                              <MinusIcon className='w-[12px] -mt-[2px] -ml-[1px]'/>
                                              :
                                              <PlusIcon className='w-[10px]'/>
                                          }
                                        </div>
                                        :
                                        null
                                    }
                                  </div>
                                </div>
                                <div className='ml-1.5'>
                                  {
                                    client.sites && ( client.isCollapsed || GlobalService.clientOrChildrensIsFilter( client, { clients: filterClients, sites: filterSites, sectors: filterSectors } ) ) ?
                                      client.sites.map( site => {
                                        return (
                                          <div key={ site.id } className='flex flex-col'>
                                            <div className='flex gap-2 items-center select-none'>
                                              <input
                                                type='checkbox'
                                                id={ site.id }
                                                className='h-fit cursor-pointer'
                                                checked={ filterSites.find( siteId => { return siteId === site.id; } ) != null }
                                                onChange={ e => { onChangeFilteredSites( site.id, ( e.target as HTMLInputElement ).checked ); } }
                                              />
                                              <label htmlFor={ site.id } className='cursor-pointer truncate text-xs'>{ site.name }</label>
                                            </div>
                                            <div className='ml-1.5'>
                                              {
                                                site.sectors ?
                                                  site.sectors.map( sector => {
                                                    return (
                                                      <div key={ sector.id } className='flex flex-col'>
                                                        <div className='flex gap-2 border-l border-gray-500 pl-2 items-center select-none'>
                                                          <input
                                                            type='checkbox'
                                                            id={ sector.id }
                                                            className='h-fit cursor-pointer'
                                                            checked={ filterSectors.find( sectorId => { return sectorId === sector.id; } ) != null }
                                                            onChange={ e => { onChangeFilteredSectors( sector.id, ( e.target as HTMLInputElement ).checked ); } }
                                                          />
                                                          <label htmlFor={ sector.id } className='truncate text-xs'>{ sector.name }</label>
                                                        </div>
                                                      </div>
                                                    );
                                                  } )
                                                  :
                                                  null
                                              }
                                            </div>
                                          </div>
                                        );
                                      } )
                                      :
                                      null
                                  }
                                </div>
                              </div>
                            );
                          } )
                        }
                        {
                          clients.length > 5 ?
                            <SeeMore
                              isSeeMore={ seeMoreClients }
                              onClick={ () => { setSeeMoreClients( !seeMoreClients ); } }
                              t={t}
                            />
                            :
                            null
                        }
                      </div>
                    }
                  </div>
                </div>
                :
                null
            }
            <div className='relative border p-1 rounded-lg text-left h-fit w-full'>
              <div className='absolute w-fit px-1.5 -top-3.5 left-3 bg-white font-bold'>{ t( 'Status' ) }</div>
              <div className='px-4 py-2 text-sm'>
                <div className='flex gap-2 items-center select-none'>
                  <input
                    type='checkbox'
                    id='disable_user'
                    className='h-fit cursor-pointer'
                    checked={ filterDisabled }
                    onChange={ e => { setFilterDisabled( ( e.target as HTMLInputElement ).checked ); } }
                  />
                  <label htmlFor='disable_user' className='cursor-pointer truncate'>{ t( 'Disable' ) }</label>
                </div>
                <div className='flex gap-2 items-center select-none'>
                  <input
                    type='checkbox'
                    id='lock_user'
                    className='h-fit cursor-pointer'
                    checked={ filterLocked }
                    onChange={ e => { setFilterLocked( ( e.target as HTMLInputElement ).checked ); } }
                  />
                  <label htmlFor='lock_user' className='cursor-pointer truncate'>{ t( 'Lock' ) }</label>
                </div>
              </div>
            </div>
            {
              skills.length != 0 ?
                <div className='relative border p-1 rounded-lg text-left h-fit w-full'>
                  <div className='absolute w-fit px-1.5 -top-3.5 left-3 bg-white font-bold'>{ t( 'Skill' ) }</div>
                  <div className='mx-4 my-2 text-sm'>
                    {
                      skills.slice( 0, seeMoreSkills ? skills.length : 5 ).map( skill => {
                        return (
                          <div
                            key={ skill.id }
                            className='flex gap-2 items-center select-none'
                          >
                            <input
                              type='checkbox'
                              id={ skill.id }
                              className='h-fit cursor-pointer'
                              checked={ filterSkills.find( skillId => { return skillId === skill.id; } ) != null }
                              onChange={ e => { onChangeFilteredSkills( skill.id, ( e.target as HTMLInputElement ).checked ); } }
                            />
                            <label htmlFor={ skill.id } className='cursor-pointer truncate'>{ skill.name }</label>
                          </div>
                        );
                      } )
                    }
                    {
                      skills.length > 5 ?
                        <SeeMore
                          isSeeMore={ seeMoreSkills }
                          onClick={ () => { setSeeMoreSkills( !seeMoreSkills ); } }
                          t={t}
                        />
                        :
                        null
                    }
                  </div>
                </div>
                :
                null
            }
            {
              abilities.length != 0 ?
                <div className='relative border p-1 rounded-lg text-left h-fit w-full'>
                  <div className='absolute w-fit px-1.5 -top-3.5 left-3 bg-white font-bold'>{ t( 'Authorization' ) }</div>
                  <div className='mx-4 my-2 text-sm'>
                    {
                      <div>
                        {
                          abilities.slice( 0, seeMoreAbilities ? abilities.length : 5 ).map( ability => {
                            return (
                              <div
                                key={ ability.id }
                                className='flex gap-2 items-center select-none'
                              >
                                <input
                                  type='checkbox'
                                  id={ ability.id }
                                  className='h-fit cursor-pointer'
                                  checked={ filterAbilities.find( abilityId => { return abilityId === ability.id; } ) != null }
                                  onChange={ e => { onChangeFilteredAbilities( ability.id, ( e.target as HTMLInputElement ).checked ); } }
                                />
                                <label htmlFor={ ability.id } className='cursor-pointer truncate'>{ ability.name }</label>
                              </div>
                            );
                          } )
                        }
                        {
                          abilities.length > 5 ?
                            <SeeMore
                              isSeeMore={ seeMoreAbilities }
                              onClick={ () => { setSeeMoreAbilities( !seeMoreAbilities ); } }
                              t={t}
                            />
                            :
                            null
                        }
                      </div>
                    }
                  </div>
                </div>
                :
                null
            }
          </div>

          <div className='flex flex-col items-center w-full h-full'>
            {
              isFilterEnd ?
                <div className='flex w-1/2 min-w-[400px] gap-2 justify-center items-center'>
                  <SearchInput
                    value={ filterName }
                    onSearched={ value => { setFilterName( value ); } }
                    t={ t }
                  />
                </div>
                :
                null
            }
            {
              !isLoading ?
                <>
                  <div className='flex flex-wrap justify-center w-full h-full'>
                    {
                      users.length != 0 ?
                        users.map( user =>  {
                          return (
                            <UserComponent
                              key={user.id}
                              user={user}
                              avatarUrl={ avatars[ user.id ] ?? null }
                              t={t}
                            />
                          );
                        } )
                        :
                        <div className='flex flex-col gap-4 w-full h-full pt-10 items-center justify-center'>
                          <UsersIcon className='w-36'/>
                          <div className='flex flex-col items-center justify-center'>
                            <span className='font-bold'>{t( 'common:oops' ) }</span>
                            <span className='font-medium'>{ t( 'No users found !' ) }</span>
                          </div>
                        </div>
                    }
                  </div>
                  <div>
                    {
                      total != 0 ?
                        (
                          <div className='flex gap-2 select-none pb-2'>
                            <ReactPaginate
                              breakLabel="•••"
                              forcePage={page}
                              onPageChange={ event => { setPage( event.selected ); } }
                              pageRangeDisplayed={2}
                              marginPagesDisplayed={1}
                              pageCount={Math.ceil( total / step )}
                              nextLabel={
                                <svg xmlns="http://www.w3.org/2000/svg" fill="#000000" height="24px" width="24px" viewBox="0 0 320 512">
                                  <path d="M278.6 233.4c12.5 12.5 12.5 32.8 0 45.3l-160 160c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3L210.7 256 73.4 118.6c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0l160 160z"/>
                                </svg>
                              }
                              previousLabel={
                                <svg xmlns="http://www.w3.org/2000/svg" fill="#000000" height="24px" width="24px" viewBox="0 0 320 512">
                                  <path d="M41.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l160 160c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L109.3 256 246.6 118.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-160 160z"/>
                                </svg>
                              }
                              renderOnZeroPageCount={null}
                              className='flex gap-2 justify-center items-center font-medium'
                              breakLinkClassName='select-none'
                              pageLinkClassName='flex justify-center items-center rounded-full w-6 h-6 hover:bg-gray-300'
                              activeLinkClassName='bg-gray-400 text-white hover:bg-gray-400'
                              nextLinkClassName='w-6 h-6 rounded-full'
                              previousLinkClassName='w-6 h-6 rounded-full'
                            />

                            <Select
                              options={ AVAILABLE_STEPS }
                              value={{ value: step, label: step }}
                              onChange={ event => { setStep( event.value ); } }
                              menuPlacement="top"
                            />
                          </div>
                        )
                        :
                        null
                    }

                    <ExportModal
                      title={ t( 'translation:export_users' ) }
                      isOpen={ isOpenExportModal }
                      onClose={ closeExportModal }
                      exportFunction={ UserService.exportUsersOfClient }
                    />
                    <ImportModal
                      title={ t( 'translation:import_users' ) }
                      isOpen={ isOpenImportModal }
                      onClose={ closeImportModal }
                      importFunction={ UserService.importUsersOnClient}
                    />
                  </div>
                </>
                :
                <Loading/>
            }
          </div>
        </div>
      </div>
    </>
  );

}